import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AmountInput } from '@/components/amount-input';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field } from '@/components/form';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, api } from '@/lib/api';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate } from '@/lib/format';
import { exponentOf } from '@/lib/currencies';
import { formatMinor, minorToPlain, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useOpenItems, type SalesDocument } from '@/lib/sales';

/** S6 "Apply to invoices": settle the customer's open invoices from a credit note (oldest first). */
export function ApplyCreditDialog({
  credit,
  open,
  onClose,
}: {
  credit: SalesDocument;
  open: boolean;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const items = useOpenItems(open ? credit.contactId : null);
  const [date, setDate] = useState(todayIso());
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  // amounts are in the document's currency (multi-currency, migration 0900)
  const ccy = credit.currency;
  const exp = exponentOf(ccy);
  const available = toMinor(credit.balance, exp) ?? 0n;

  useEffect(() => {
    if (!open || !items.data) return;
    setDate(todayIso() < credit.issueDate ? credit.issueDate : todayIso());
    const next: Record<string, string> = {};
    let left = available;
    const invoices = [...items.data.invoices].sort((a, b) =>
      a.id === credit.relatedDocumentId ? -1 : b.id === credit.relatedDocumentId ? 1 : 0,
    );
    for (const inv of invoices) {
      if (left <= 0n) break;
      const bal = toMinor(inv.balance, exp) ?? 0n;
      const take = bal < left ? bal : left;
      next[inv.id] = minorToPlain(take, exp);
      left -= take;
    }
    setAmounts(next);
  }, [open, items.data, available, credit.issueDate, credit.relatedDocumentId]);

  const total = Object.values(amounts).reduce((s, v) => s + (toMinor(v, exp) ?? 0n), 0n);
  const m = useMutation({
    mutationFn: () =>
      api<SalesDocument>(`/orgs/${orgId}/sales/documents/${credit.id}/apply`, {
        method: 'POST',
        body: JSON.stringify({
          date,
          allocations: Object.entries(amounts)
            .filter(([, v]) => (toMinor(v, exp) ?? 0n) > 0n)
            .map(([documentId, amount]) => ({ documentId, amount })),
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries();
      toast(`Credit note ${credit.number}: ${ccy} ${formatMinor(total, exp)} applied.`);
      onClose();
    },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Apply credit note {credit.number}</DialogTitle>
          <DialogDescription>
            {ccy} {formatAmount(credit.balance)} available for {credit.contactName}. Applying posts
            nothing new: the credit note already reduced accounts receivable.
          </DialogDescription>
        </DialogHeader>
        {m.error ? (
          <ErrorBanner>
            {m.error instanceof ApiError ? m.error.message : String(m.error)}
          </ErrorBanner>
        ) : null}
        <Field label="Date applied" className="w-[200px]">
          {(id) => <DateInput id={id} value={date} onChange={setDate} />}
        </Field>
        {items.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.data?.invoices.length ? (
          <table className="w-full text-sm" aria-label="Open invoices">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr className="border-b">
                <th className="py-2">Invoice</th>
                <th className="py-2">Due date</th>
                <th className="py-2 text-right">Open balance</th>
                <th className="w-[160px] py-2 text-right">Apply</th>
              </tr>
            </thead>
            <tbody>
              {items.data.invoices.map((inv) => (
                <tr key={inv.id} className="border-b last:border-0">
                  <td className="py-1.5">{inv.number}</td>
                  <td className="py-1.5">{inv.dueDate ? formatDate(inv.dueDate) : ''}</td>
                  <td className="tnum py-1.5 text-right">{formatAmount(inv.balance)}</td>
                  <td className="py-1">
                    <AmountInput
                      exp={exp}
                      aria-label={`Apply to ${inv.number}`}
                      value={amounts[inv.id] ?? ''}
                      onValueChange={(v) => setAmounts((a) => ({ ...a, [inv.id]: v }))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-muted-foreground">This customer has no open invoices.</p>
        )}
        <p
          className={`text-sm ${total > available ? 'text-destructive' : 'text-muted-foreground'}`}
        >
          Applying {ccy} {formatMinor(total, exp)} of {ccy} {formatMinor(available, exp)}.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={m.isPending || total === 0n || total > available}
            onClick={() => m.mutate()}
          >
            {m.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
